/**
 * ADM-07 课程商品只读详情抽屉（course-product-catalog:SC-26/SC-33~SC-35 前端）。
 *
 * 列表点击商品名称打开：Descriptions 基本信息/定价分期/卖点/SKU 配置清单 + 家长端预览卡 +
 * 「销售历史记录」分页表（下单时间倒序，监护人手机号脱敏；一期空态文案，4-1 注入真实数据源；
 * 订单号下钻按钮在 4-1 订单页就绪前禁用，design Q5）。
 */
import { useEffect, useState } from 'react';
import { Alert, Descriptions, Drawer, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { productApi, type ProductItem, type SalesHistoryItem } from '../../api/products.api';
import { shelfStatusLabel } from '../../api/labels';
import { EllipsisText } from '../../components/EllipsisCell';
import { MoneyDisplay } from '../../components/MoneyDisplay';

const { Text, Title } = Typography;

/** 状态标签颜色映射（商品三值）。 */
const statusColor: Record<string, string> = {
  DRAFT: 'default',
  ON_SHELF: 'green',
  OFF_SHELF: 'orange',
};

export interface ProductDetailDrawerProps {
  /** 当前查看的商品 */
  product: ProductItem | null;
  /** 关闭抽屉 */
  onClose: () => void;
}

/** 报名来源文案。 */
const sourceLabel: Record<string, string> = {
  PARENT_SELF: '家长自助',
  STAFF_ASSISTED: '员工代建',
};

export function ProductDetailDrawer({ product, onClose }: ProductDetailDrawerProps) {
  const open = product !== null;
  const [history, setHistory] = useState<SalesHistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  // 打开时加载销售历史第一页（只读 CQRS，一期空态）
  useEffect(() => {
    if (!open || !product) return;
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryPage(1);
    productApi
      .listSalesHistory(product.id, 1, 20)
      .then((res) => {
        if (cancelled) return;
        setHistory(res.items);
        setHistoryTotal(res.total);
      })
      .catch(() => undefined)
      .finally(() => !cancelled && setHistoryLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, product]);

  const fetchHistoryPage = async (page: number) => {
    if (!product) return;
    setHistoryLoading(true);
    setHistoryPage(page);
    try {
      const res = await productApi.listSalesHistory(product.id, page, 20);
      setHistory(res.items);
      setHistoryTotal(res.total);
    } finally {
      setHistoryLoading(false);
    }
  };

  const historyColumns: ColumnsType<SalesHistoryItem> = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      width: 150,
      ellipsis: true,
      render: (orderNo: string) => <EllipsisText value={orderNo} />,
    },
    { title: '学生', dataIndex: 'studentName', width: 90 },
    { title: '监护人', dataIndex: 'guardianMobileMasked', width: 120 },
    { title: '校区', dataIndex: 'campusName', width: 100, ellipsis: true },
    {
      title: '应收',
      dataIndex: 'receivableAmount',
      width: 90,
      render: (v: number) => <MoneyDisplay amountInCents={v} />,
    },
    {
      title: '实收',
      dataIndex: 'paidAmount',
      width: 90,
      render: (v: number) => <MoneyDisplay amountInCents={v} />,
    },
    { title: '订单状态', dataIndex: 'status', width: 90 },
    {
      title: '来源',
      dataIndex: 'source',
      width: 80,
      render: (v: string) => sourceLabel[v] ?? v,
    },
    {
      title: '下单时间',
      dataIndex: 'createdAt',
      width: 150,
      render: (v: string) => new Date(v).toLocaleString('zh-CN'),
    },
  ];

  if (!product) {
    return <Drawer open={false} onClose={onClose} />;
  }
  return (
    <Drawer
      open={open}
      width={720}
      title={`商品详情 · ${product.name}`}
      onClose={onClose}
      destroyOnHidden
    >
      {product.orderRefCount > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`已被 ${product.orderRefCount} 个订单引用；历史订单以下单时快照为准，不受商品后续修改影响`}
        />
      )}
      <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="归属省/市">{`${product.provinceName} · ${product.cityName}`}</Descriptions.Item>
        <Descriptions.Item label="状态">
          <Tag color={statusColor[product.status]}>{shelfStatusLabel[product.status]}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="商品定价">
          <MoneyDisplay amountInCents={product.price} />
        </Descriptions.Item>
        <Descriptions.Item label="原价（SKU 合计）">
          <Text delete>
            <MoneyDisplay amountInCents={product.originalPrice} />
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="首付款比例">
          {(product.depositRatio * 100).toFixed(0)}%
        </Descriptions.Item>
        <Descriptions.Item label="尾款比例（自动）">
          {(product.finalRatio * 100).toFixed(0)}%
        </Descriptions.Item>
        <Descriptions.Item label="尾款周期">{product.finalDueDays} 天</Descriptions.Item>
        <Descriptions.Item label="订单引用数">{product.orderRefCount}</Descriptions.Item>
        <Descriptions.Item label="卖点标签" span={2}>
          {product.features.length ? product.features.map((f) => <Tag key={f}>{f}</Tag>) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="最近上架时间" span={2}>
          {product.publishedAt ? new Date(product.publishedAt).toLocaleString('zh-CN') : '未上架'}
        </Descriptions.Item>
      </Descriptions>

      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        SKU 配置清单
      </Text>
      {product.skuConfigs.map((c) => (
        <div
          key={c.id}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            padding: '6px 12px',
            marginBottom: 6,
          }}
        >
          <span>
            {c.skuName ?? c.skuId}
            <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
              {c.subjectName}
            </Text>
          </span>
          <span>
            {/* O-14 逐项形态：期限行显示期限天数与卡面行标价（历史数据缺省按课时包渲染） */}
            <Text type="secondary" style={{ marginLeft: 12 }}>
              {c.chargeMode === 'PERIOD'
                ? `期限 ${c.periodDays ?? '-'} 天（畅学）`
                : `${c.lessonCount ?? '-'} 课时`}
            </Text>
            <Text type="secondary" style={{ marginLeft: 12 }}>
              行标价 <MoneyDisplay amountInCents={c.listPrice ?? 0} />
            </Text>
            <Text type="secondary" style={{ marginLeft: 12 }}>
              赠试课 {c.trialSessionCount ?? '-'} 次
              {typeof c.trialSessionCountOverride === 'number' ? '（试课已单独设置）' : ''}
            </Text>
          </span>
        </div>
      ))}

      <Title level={5} style={{ marginTop: 20 }}>
        家长端预览
      </Title>
      <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fafafa' }}>
        {product.coverImageUrl ? (
          <img
            src={product.coverImageUrl}
            alt="封面"
            style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }}
          />
        ) : (
          <div style={{ width: '100%', height: 120, background: '#e3f2ef', borderRadius: 8, marginBottom: 8 }} />
        )}
        <Text strong>{product.name}</Text>
        <div style={{ margin: '6px 0' }}>
          {product.features.map((f) => (
            <Tag key={f} color="orange">
              {f}
            </Tag>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Text delete type="secondary" style={{ fontSize: 12 }}>
            ¥{(product.originalPrice / 100).toFixed(2)}
          </Text>
          <Text style={{ fontSize: 20, color: '#fa8c16', fontWeight: 700 }}>
            ¥{(product.price / 100).toFixed(2)}
          </Text>
        </div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          首付款比例 {(product.depositRatio * 100).toFixed(0)}%，首付结清后 {product.finalDueDays} 天内交齐尾款
        </Text>
      </div>

      <Title level={5} style={{ marginTop: 20 }}>
        销售历史记录（共 {historyTotal} 条）
      </Title>
      <Table<SalesHistoryItem>
        scroll={{ x: 'max-content' }}
        rowKey="orderId"
        size="small"
        columns={historyColumns}
        dataSource={history}
        loading={historyLoading}
        locale={{ emptyText: '订单模块就绪后展示销售历史' }}
        pagination={{
          current: historyPage,
          pageSize: 20,
          total: historyTotal,
          showSizeChanger: false,
          onChange: (p) => void fetchHistoryPage(p),
        }}
      />
    </Drawer>
  );
}
